export const REDACTED_VALUE = 'REDACTED_REENTER_MANUALLY';

function normalizeKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function hasSafeTokenMeaning(normalizedKey: string): boolean {
  return (
    /(?:url|uri|field|name|type|method|mode|config|configured|response|request|id)$/.test(normalizedKey) ||
    /token(?:refresh)?(?:time|timestamp|interval|seconds|minutes|hours|millis|milliseconds)$/.test(normalizedKey) ||
    /token(?:expiration|expiry|expiresat|expiresin|lifetime|ttl)$/.test(normalizedKey) ||
    normalizedKey.endsWith('newtokenonstartup')
  );
}

export function isSensitiveKey(key: string): boolean {
  const normalizedKey = normalizeKey(key);
  if (!normalizedKey) return false;

  if (
    normalizedKey === 'authenticationkey' ||
    normalizedKey.endsWith('authenticationkey') ||
    normalizedKey === 'authkey' ||
    normalizedKey.endsWith('authkey') ||
    normalizedKey === 'proxypass' ||
    normalizedKey.endsWith('proxypass') ||
    normalizedKey.includes('password') ||
    normalizedKey.includes('passwd') ||
    normalizedKey.includes('passphrase') ||
    normalizedKey.includes('secret') ||
    normalizedKey.includes('apikey') ||
    normalizedKey.includes('privatekey') ||
    normalizedKey.includes('sharedkey') ||
    normalizedKey.includes('subscriptionkey') ||
    normalizedKey.includes('credential') ||
    normalizedKey.includes('bearer')
  ) {
    return true;
  }

  if (normalizedKey === 'authorization' || normalizedKey.endsWith('authorization')) {
    return true;
  }

  if (normalizedKey.includes('token') && !hasSafeTokenMeaning(normalizedKey)) {
    return true;
  }

  return false;
}

function isStructuredSecretContainerKey(key: string): boolean {
  const normalizedKey = normalizeKey(key);
  return (
    normalizedKey.endsWith('headers') ||
    normalizedKey.endsWith('urlparameters') ||
    normalizedKey.endsWith('clientparameters') ||
    normalizedKey.endsWith('parameters') ||
    normalizedKey.endsWith('postbody') ||
    normalizedKey.endsWith('messagebody') ||
    normalizedKey.endsWith('body')
  );
}

function redactUrlQuerySecrets(rawUrl: string): string {
  try {
    const parsed = new URL(rawUrl);
    let changed = false;

    for (const key of Array.from(parsed.searchParams.keys())) {
      if (isSensitiveKey(key)) {
        parsed.searchParams.set(key, REDACTED_VALUE);
        changed = true;
      }
    }

    return changed ? parsed.toString() : rawUrl;
  } catch {
    return rawUrl;
  }
}

function redactJsonText(text: string): string | undefined {
  const trimmed = text.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return undefined;

  try {
    const parsed = JSON.parse(trimmed) as unknown;
    const redacted = redactSensitiveObject(parsed);
    const redactedText = JSON.stringify(redacted);
    return redactedText === trimmed ? undefined : redactedText;
  } catch {
    return undefined;
  }
}

function redactDelimitedSecrets(text: string): string {
  return text.replace(
    /(^|[;&,\n\r]\s*)([A-Za-z0-9_.-]+)(\s*[:=]\s*)([^;&,\n\r]+)/g,
    (match, prefix: string, key: string, separator: string) => {
      if (!isSensitiveKey(key)) return match;
      return `${prefix}${key}${separator}${REDACTED_VALUE}`;
    },
  );
}

export function redactSensitiveText(text: string): string {
  const jsonRedaction = redactJsonText(text);
  if (jsonRedaction !== undefined) return jsonRedaction;

  const withRedactedUrls = text.replace(/\bhttps?:\/\/[^\s<>"')]+/g, (rawUrl) => redactUrlQuerySecrets(rawUrl));
  return redactDelimitedSecrets(withRedactedUrls);
}

export function redactSensitiveValue(key: string, value: unknown): unknown {
  if (value === null || value === undefined) return value;

  const keyIsSensitive = isSensitiveKey(key);
  const keyIsStructuredContainer = isStructuredSecretContainerKey(key);

  if (typeof value === 'string') {
    if (keyIsStructuredContainer) return redactSensitiveText(value);
    if (keyIsSensitive && value.length > 0) return REDACTED_VALUE;
    return redactSensitiveText(value);
  }

  if (typeof value !== 'object') {
    return keyIsSensitive ? REDACTED_VALUE : value;
  }

  if (keyIsSensitive && !keyIsStructuredContainer) {
    return REDACTED_VALUE;
  }

  return redactSensitiveObject(value);
}

export function redactSensitiveObject<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((item) => (typeof item === 'string' ? redactSensitiveText(item) : redactSensitiveObject(item))) as T;
  }

  if (typeof value !== 'object' || value === null) {
    return value;
  }

  if (value instanceof Date || value instanceof URL) {
    return value;
  }

  const redactedEntries = Object.entries(value as Record<string, unknown>).map(([key, entryValue]) => [
    key,
    redactSensitiveValue(key, entryValue),
  ]);

  return Object.fromEntries(redactedEntries) as T;
}
